import type { useIncomingLinksQuery$data } from '../hooks/__generated__/useIncomingLinksQuery.graphql.ts';

// Keep only ARI-shaped column values (same `ari:` guard the TWG graph explorer
// applies before hydrating a node); non-ARI scalars are skipped here. Note this
// does NOT exclude external-graph peer ids like `ari:cloud:graph::worker/...` —
// those pass the guard but simply fail to resolve in ORS and get dropped there.
const isAri = (value: unknown): value is string =>
	typeof value === 'string' && value.startsWith('ari:');

/**
 * Pulls the ordered, de-duplicated list of entity ARIs out of an
 * `graphStore_cypherQueryV2` result. Each returned node is a
 * `GraphStoreCypherQueryV2AriNode` whose `id` is the ARI of the subject entity;
 * everything else in the column union is ignored. Order follows the query's
 * `ORDER BY entity.lastUpdated DESC`.
 */
export const extractIncomingLinkAris = (
	data: useIncomingLinksQuery$data | null | undefined,
): string[] => {
	// These chained iterators are linear, not quadratic: work is bounded by the
	// query's `first` (≤1000 rows, each with a single column), so the perf rule's
	// heuristic is a false positive here.
	// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
	const aris = (data?.graphStore_cypherQueryV2?.edges ?? [])
		.flatMap((edge) => edge?.node?.columns ?? [])
		.map((column) => column?.value?.id)
		.filter(isAri);

	// Set preserves first-occurrence insertion order, so this de-dupes while
	// keeping the query's `ORDER BY entity.lastUpdated DESC` ordering.
	return [...new Set(aris)];
};
